import { Discount } from "../../../../../shared/types/src/lib/price/discount.interface";
import { Promotion } from "../../../../../shared/types/src/lib/price/promotion.interface";
import { ConfigurationType } from "@icc/shared/types";
import { Photo } from "../interfaces/photo.interface";
import { _ } from "@icc/shared/helpers";
import { DocumentPosition } from "./document-position.model";

export interface SalesDocumentPosition extends DocumentPosition {

    /**
     * Typ konstrukcji, np. static, complex, external_blind, window, group
     */
    type: 'static'
        | ConfigurationType
        | 'group'
        | 'additional';

    /**
     * Id konfiguratora uniwersalnego, który został użyty do utworzenia pozycji
     */
    configuratorId?: number;

    /**
     * Identyfikator produktu (jeśli pozycja zawiera produkt)
     */
    productId?: number;

    /**
    * Id dodatkowej jednostki cenowej, przy użyciu ktorej pozycja została wyceniona
    */
    priceUnitId: number;

    /**
     * Kod wbudowanej jednostki cenowej, przy użyciu ktorej pozycja została wyceniona
     */
    priceUnitCode: string;

    /**
     * Marża w procentach
     */
    priceMarginPercent: number;

    /**
     * Waluta w postaci kodu zgodnego ze standardem ISO 4217
     */
    priceCurrency: string;

    /**
     * Stawka podatku VAT
     */
    priceTaxRate: number;

    /**
     * Id stawki podatku VAT
     */
    priceTaxRateId: number;

    /**
     * Czy kwota jest brutto?
     */
    priceIsGross: boolean;

    /**
     * Wartość przed promocją i rabatami
     */
    priceBeforePromotionsValue: number;

    /**
     * Promocje w postaci JSONa
     */
    pricePromotions: Promotion[];

    /**
     * Wartość przed rabatami
     */
    priceBeforeDiscountsValue: number;

    /**
     * Rabaty w postaci JSONa
     */
    priceDiscounts: Discount[];

    /**
     * Wartość po rabatach
     */
    priceAfterDiscountsValue: number;

    /**
     * Wartość sprzedaży zmodyfikowana przez użytkownika
     * (jeśli użytkownik wprowadził własną wartość sprzedaży)
     */
    customPriceValue?: boolean;

    /**
     * Waluta bazowa sprzedaży w postaci kodu zgodnego ze standardem ISO 4217
     */
    basePriceCurrency: string;

    /**
     * Wartość bazowa sprzedaży
     */
    basePriceValue: number;

    /**
     * Stawka podatku VAT ceny bazowej sprzedaży
     */
    basePriceTaxRate: number;

    /**
     * Id stawki podatku VAT ceny bazowej sprzedaży
     */
    basePriceTaxRateId: number;

    /**
     * Czy kwota bazowa sprzedaży jest brutto?
     */
    basePriceIsGross: boolean;

    /**
     * Waluta bazowa zakupu w postaci kodu zgodnego ze standardem ISO 4217
     */
    baseBuyPriceCurrency?: string | null;

    /**
     * Wartość bazowa zakupu
     */
    baseBuyPriceValue?: number | null;

    /**
     * Stawka podatku VAT ceny bazowej zakupu
     */
    baseBuyPriceTaxRate?: number | null;

    /**
     * Id stawki podatku VAT ceny bazowej zakupu
     */
    baseBuyPriceTaxRateId?: number | null;

    /**
     * Czy kwota bazowa zakupu jest brutto?
     */
    baseBuyPriceIsGross?: boolean | null;


    /**
     * Termin ważności
     */

    validityDate?:Date;

    /**
     * Data wyliczania ceny
     */
    priceCalculationDate: Date;

    /**
     * Czy pozycja posiada pasujące jednostki w innej wersji cenowej
    */
    hasMatchingPrices?: boolean;

    /**
     * Rewizje cen w postaci
     */
    pricesRevisions: {
        prices: number;
        priceRasters?: number;
        discounts?: number;
        promotions?: number;
        charge?: number;
        multiplier?: number;
        marketFactor?: number;
    };

    /**
     * Ceny sprawdzone po stronie serwera
     *
     * Wartości:
     * - 'valid' - cena została sprawdzona po stronie serwera i jest poprawna
     * - 'corrected' - cena została sprawdzona po stronie serwera i została poprawiona
     * - 'pending' - cena nie została jeszcze sprawdzona po stronie serwera lub nie została jeszcze poprawiona
     */
    priceValidationStatus: 'valid' | 'corrected' | 'pending';
    /**
     * Czy pozycja jest dostarczona
     */
    isDelivered?: boolean;
    /**
     * Język
     */
    lang?: string;
}

export type SalesDocumentPositionDTO = Omit<
    SalesDocumentPosition,
    'configuration' | 'priceDiscounts' | 'pricePromotions' | 'department' | 'photos' | '$dirty' | 'createdServer' | 'title' | 'slaves' | 'masters' | 'isDelivered' | 'pricesRevisions' | 'lang'
> & {
    title?: string;
    configuration?: string;
    priceDiscounts?: string;
    pricePromotions?: string;
    department?: string;
    photos?: string;
    slaves?: string;
    masters?: string;
    pricesRevisions?: string;
    lang?: string;
};

export type SalesDocumentPositionPrices = Partial<Pick<
    SalesDocumentPosition,
    | 'priceCalculationDate'
    | 'priceBeforePromotionsValue'
    | 'priceBeforeDiscountsValue'
    | 'priceAfterDiscountsValue'
    | 'pricePromotions'
    | 'priceDiscounts'
    | 'priceCurrency'
    | 'priceTaxRate'
    | 'priceTaxRateId'
    | 'priceIsGross'
    | 'validityDate'
    | 'pricesRevisions'
    >>;

export type SalesDocumentPositionBasePrices = Partial<Pick<
    SalesDocumentPosition,
    | 'basePriceValue'
    | 'basePriceCurrency'
    | 'basePriceTaxRate'
    | 'basePriceTaxRateId'
    | 'basePriceIsGross'
    | 'baseBuyPriceValue'
    | 'baseBuyPriceCurrency'
    | 'baseBuyPriceTaxRate'
    | 'baseBuyPriceTaxRateId'
    | 'baseBuyPriceIsGross'
    | 'pricesRevisions'
    >>;

export const SalesDocumentPositionNonReadableProps = [
    'id',
    'uuid',
    'externalSource',
    'parentId',
    'rootId',
    'documentId',
    'type',
    'basePriceValue',
    'basePriceCurrency',
    'basePriceTaxRate',
    'basePriceTaxRateId',
    'basePriceIsGross',
    'baseBuyPriceValue',
    'baseBuyPriceCurrency',
    'baseBuyPriceTaxRate',
    'baseBuyPriceTaxRateId',
    'baseBuyPriceIsGross',
    'priceTaxRateId',
    'created',
    'modified',
    'createdServer',
    'modifiedServer',
    'rootCreated',
    'validityDate',
    'priceCalculationDate',
    'deletedAt',
    'machineId',
    'errors',
    'hasMatchingPrices',
    '$dirty',
    'department',
    'photos',
    'slaves',
    'masters',
    'productId',
    'configuratorId',
    'pricesRevisions',
    'priceValidationStatus',
] as const satisfies readonly (keyof SalesDocumentPosition)[];

export const SalesDocumentPositionReadablePropsNames: Record<Exclude<keyof SalesDocumentPosition, typeof SalesDocumentPositionNonReadableProps[number]>, string> = {
    externalId: _('SALES_DOCUMENT_POSITION|Zewnętrzne ID'),
    isDeleted: _('SALES_DOCUMENT_POSITION|Usunięta'),
    isInvalid: _('SALES_DOCUMENT_POSITION|Nieprawidłowa'),
    description: _('SALES_DOCUMENT_POSITION|Opis'),
    title: _('SALES_DOCUMENT_POSITION|Tytuł'),
    lang: _('SALES_DOCUMENT_POSITION|Język'),
    name: _('SALES_DOCUMENT_POSITION|Nazwa'),
    quantity: _('SALES_DOCUMENT_POSITION|Ilość'),
    configuration: _('SALES_DOCUMENT_POSITION|Konfiguracja'),
    priceMarginPercent: _('SALES_DOCUMENT_POSITION|Marża procentowa'),
    priceCurrency: _('SALES_DOCUMENT_POSITION|Waluta'),
    priceIsGross: _('SALES_DOCUMENT_POSITION|Czy brutto?'),
    priceBeforePromotionsValue: _('SALES_DOCUMENT_POSITION|Wartość przed promocjami'),
    pricePromotions: _('SALES_DOCUMENT_POSITION|Promocje'),
    priceBeforeDiscountsValue: _('SALES_DOCUMENT_POSITION|Wartość przed rabatami'),
    priceDiscounts: _('SALES_DOCUMENT_POSITION|Rabaty'),
    priceAfterDiscountsValue: _('SALES_DOCUMENT_POSITION|Wartość po rabatach'),
    priceUnitId: _('SALES_DOCUMENT_POSITION|Jednostka cenowa'),
    priceUnitCode: _('SALES_DOCUMENT_POSITION|Jednostka cenowa'),
    priceTaxRate: _('SALES_DOCUMENT_POSITION|Stawka podatku VAT'),
    customPriceValue: _('SALES_DOCUMENT_POSITION|Wyceniona wartość'),
    isDelivered: _('SALES_DOCUMENT_POSITION|Dostarczona'),
};
